import { useEffect, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { FileSelectionContainerType } from "@/Common/Models/FileSelection/FileSelectionContainerType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { FileSelectionContainer } from "@/ExportableFile/Components/FileSelection/FileSelectionContainer";
import { FileSelectionSettingComponentState } from "@/ExportableFile/Components/FileSelection/FileSelectionSettingComponent";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { FileTypeMedicalSummary } from "@/ExportableFile/Models/FileTypes/FileTypeMedicalSummary";
import { FileTypeMedicationSummary } from "@/ExportableFile/Models/FileTypes/FileTypeMedicationSummary";
import { ConsentStatus } from "@/Patient/Models/Consent/ConsentEnum";
import { AccessWithoutConsentDialog } from "../Consent/AccessWithoutConsentDialog";
import { ConsentAction } from "../Consent/Actions/ConsentAction";
import { ConsentProgramDialog } from "../Consent/ConsentProgramDialog";
import { PatientImportDialog } from "./PatientImportDialog";
import { SearchClinicDialog } from "./SearchClinicDialog";

interface IExternalPatientFileImportComponentProps {
    model: IPatientBannerModel;
    onClose: () => void;
}

function ExternalPatientFileImportComponent({ model, onClose }: IExternalPatientFileImportComponentProps): JSX.Element {
    const [showConsentProgramDialog, setShowConsentProgramDialog] = useState(false);
    const [showAccessWithoutConsentDialog, setShowAccessWithoutConsentDialog] = useState(false);
    const [showSearchClinicDialog, setShowSearchClinicDialog] = useState(false);
    const [showPatientImportDialog, setShowPatientImportDialog] = useState(false);
    const [showFileSelectionContainer, setShowFileSelectionContainer] = useState(false);
    const [clientTag, setClientTag] = useState(null);
    const [patientFound, setPatientFound] = useState(null);
    const [consentProgramId, setConsentProgramId] = useState(0);
    const [claimId, setClaimId] = useState(null);

    const patient = model.get("Model");

    const person = Person.FromPatient(patient);

    useEffect(() => {
        (async () => {
            const action = new ConsentAction();
            const response = await action.accessWithoutConsentPermission(person.ContactId, "XCOM");
            if (response != null) {
                setClaimId(response.ClaimId);
                if (response.HasAccess) {
                    setShowSearchClinicDialog(true);
                }
                else {
                    setShowConsentProgramDialog(true);
                }
            }
        })();
    }, [person.ContactId, claimId]);

    const onClinicSelected = (clientTag: string): void => {
        setShowPatientImportDialog(true);
        setClientTag(clientTag);
    };

    const savePatientConsent = (status: ConsentStatus): void => {
        sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_LoadConsentProgramHistory, { PatientId: person.ContactId });

        setShowConsentProgramDialog(false);

        if (status === ConsentStatus.Consented) {
            setShowSearchClinicDialog(true);
        }
        else {
            onClose();
        }
    };

    const onAccessWithoutConsentClick = (programId: number): void => {
        setShowConsentProgramDialog(false);
        setShowAccessWithoutConsentDialog(true);
        setConsentProgramId(programId);
    };

    const backToConsentProgramDialog = (): void => {
        setShowAccessWithoutConsentDialog(false);
        setShowConsentProgramDialog(true);
    };

    const onPatientSelected = (patient: Patient): void => {
        setShowSearchClinicDialog(false);
        setShowPatientImportDialog(false);
        setPatientFound(patient);
        setShowFileSelectionContainer(true);
    };

    const backToSearchClinicDialog = (): void => {
        setShowPatientImportDialog(false);
        setShowSearchClinicDialog(true);
    };

    const backToPatientImportDialog = (): void => {
        setShowFileSelectionContainer(false);
        setShowPatientImportDialog(true);
    };

    const onCloseSearchClinicDialog = (): void => {
        setShowSearchClinicDialog(false);
        onClose();
    };

    const onCloseConsentProgramDialog = (): void => {
        setShowConsentProgramDialog(false);
        onClose();
    };

    const onCloseAccessWithoutConsentDialog = (): void => {
        setShowAccessWithoutConsentDialog(false);
        onClose();
    };

    const onClosePatientImportDialog = (): void => {
        setShowPatientImportDialog(false);
        onClose();
    };

    const onCloseFileSelectionContainer = (): void => {
        setShowFileSelectionContainer(false);
        onClose();
    };

    const onSaveAccessWithoutConsent = (claimId: string): void => {
        setClaimId(claimId);
        setShowAccessWithoutConsentDialog(false);
        setShowSearchClinicDialog(true);
    };

    const loadFileList = async (patient: Person, setting?: FileSelectionSettingComponentState): Promise<ClientResponse<any>> => {
        let request = {
            PatientContactId: patient.ContactId,
            DateFrom: setting.dateFrom,
            DateTo: setting.dateTo,
            Categories: setting.categories.filter(c => c.Checked == true).map(c => c.Id).toArray(),
            LabCriterias: null,
            clientTag: clientTag,
            claimId: claimId
        };
        let files = await Client.postAsync(
            ServerOperations.Server.Myle_ExternalCommunication_SearchPatientPrintableFiles,
            request
        );
        return files;
    };

    const addSummaries = (fileList: Immutable.List<ExportableFileModel>, patientContactId: string): Immutable.List<ExportableFileModel> => {
        const fileTypeMedicalSummary = new FileTypeMedicalSummary(patientContactId);

        fileTypeMedicalSummary.isExternal = true;
        fileTypeMedicalSummary.clientTag = clientTag;
        fileTypeMedicalSummary.claimId = claimId;

        const fileTypeMedicationSummary = new FileTypeMedicationSummary(patientContactId);

        fileTypeMedicationSummary.isExternal = true;
        fileTypeMedicationSummary.clientTag = clientTag;
        fileTypeMedicationSummary.claimId = claimId;

        return fileList
            .push(fileTypeMedicalSummary.toExportableFileModel())
            .push(fileTypeMedicationSummary.toExportableFileModel());
    };

    return (
        <>
            {showSearchClinicDialog && (
                <SearchClinicDialog
                    onClose={() => onCloseSearchClinicDialog()}
                    onSelected={employee => onClinicSelected(employee.ClientTag)}
                    visible={!showPatientImportDialog}
                />
            )}

            {showConsentProgramDialog && (
                <ConsentProgramDialog
                    person={person}
                    onSave={status => savePatientConsent(status)}
                    onClose={() => onCloseConsentProgramDialog()}
                    onAccessWithoutConsent={programId => onAccessWithoutConsentClick(programId)}
                    code="XCOM"
                    showAccessWithoutConsent
                />
            )}

            {showAccessWithoutConsentDialog && (
                <AccessWithoutConsentDialog
                    patientContactFacetId={person.ContactFacetId}
                    onClose={() => onCloseAccessWithoutConsentDialog()}
                    onBack={() => backToConsentProgramDialog()}
                    consentProgramId={consentProgramId}
                    onSave={claimId => onSaveAccessWithoutConsent(claimId)}
                />
            )}

            {showPatientImportDialog && (
                <PatientImportDialog
                    clientTag={clientTag}
                    model={model}
                    onClose={() => onClosePatientImportDialog()}
                    onBack={() => backToSearchClinicDialog()}
                    onSelected={patient => onPatientSelected(patient)}
                    patient={patient}
                    claimId={claimId}
                />
            )}

            {showFileSelectionContainer && (
                <FileSelectionContainer
                    title={cacheStore.GetString("ImportFilesLabel")}
                    patient={Person.FromPatient(patientFound)}
                    type={FileSelectionContainerType.ImportFiles}
                    onClose={() => onCloseFileSelectionContainer()}
                    onBack={() => backToPatientImportDialog()}
                    loadFileList={(person, setting) => loadFileList(person, setting)}
                    addSummaries={(fileList, patientContactId) => addSummaries(fileList, patientContactId)}
                    autoManageOverlay
                    popupStyle={{ textAlign: "left" }}
                />
            )}

        </>
    );
}

export { ExternalPatientFileImportComponent };
